import {
  assertIsUUID,
  generateUUID,
  type UUID,
} from '@bizzdesign/bizzdesign-sdk/uuid';
import { Injectable, inject } from '@angular/core';
import { type Observable, map } from 'rxjs';
import { SessionService } from '@bizzdesign/auth';
import { assertIsSystemAttributesUser } from '@bizzdesign/sdk-knowledgegraph';
import { type SystemAttributesUser } from '@bizzdesign/shared/models';
import { MarkdownValue } from '@bizzdesign/shared/dom-models';
import {
  type Workspace,
  type WorkspaceBase,
} from '@bizzdesign/data-access-workspaces';
import type { WorkspaceCreate } from '@bizzdesign/ai-assistant/shared';
import { FrameFactoryService } from './frame-factory.service';

@Injectable()
export class WorkspaceFactoryService {
  private readonly sessionService = inject(SessionService);
  private readonly frameFactoryService = inject(FrameFactoryService);
  createWorkspace(id?: UUID, name?: string): Observable<Workspace> {
    return this.sessionService.getAuthenticatedUser().pipe(
      map((user) => {
        assertIsSystemAttributesUser(user);
        const systemAttributesUser: SystemAttributesUser = {
          userId: user.userId,
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
        };
        return {
          id: id ?? generateUUID(),
          name: name ?? 'Untitled',
          archived: false,
          objective: null,
          users: [systemAttributesUser],
          createdAt: new Date(),
          editedAt: new Date(),
          createdBy: systemAttributesUser,
          updatedBy: systemAttributesUser,
        };
      }),
    );
  }

  /**
   * Build a new workspace with the name and objective a user typed.
   *
   * @param name - Name for the workspace.
   * @param objective - Objective text; blank text leaves the objective empty.
   * @returns The in-memory workspace, owned by the authenticated user.
   */
  createWorkspaceWithObjective(
    name: string,
    objective: string,
  ): Observable<Workspace> {
    return this.createWorkspace(undefined, name).pipe(
      map((workspace) => ({
        ...workspace,
        objective: this.objectiveTextToMarkdown(objective),
      })),
    );
  }

  private objectiveTextToMarkdown(objective: string): MarkdownValue | null {
    const text = objective.trim();
    return text === ''
      ? null
      : new MarkdownValue({ ops: [{ insert: `${text}\n` }] });
  }

  createWorkspaceBase(id: string, name: string): WorkspaceBase {
    assertIsUUID(id);
    return {
      id,
      name: name,
      archived: false,
    };
  }
  createWorkspaceForWorkspaceCreate(
    workspace: WorkspaceBase,
    create: WorkspaceCreate,
  ): Observable<Workspace> {
    return this.createWorkspace(workspace.id, workspace.name).pipe(
      map((workspace) => ({
        ...workspace,
        name: create.name,
        frames:
          create.frameCreates?.map((frameCreate) =>
            this.frameFactoryService.createFrameForFrameCreate(frameCreate),
          ) ?? [],
        objective:
          create.objective instanceof MarkdownValue
            ? create.objective
            : this.objectiveTextToMarkdown(create.objective),
      })),
    );
  }
}
